import api, { authApi, API_ENDPOINTS } from '../config/api';
import LeadAssignmentNotificationService from './leadAssignmentNotificationService';
import { jwtDecode } from 'jwt-decode';

// Proper Leads Service for managing leads using dedicated leads endpoint
// Connected to MongoDB database via API
export class LeadsService {
  
  // Create a new lead
  static async createLead(leadData) {
    try {
      console.log('🔄 Creating lead with data:', leadData);
      console.log('🔄 Custom fields from form:', leadData.customFields);
      
      // Use customFields directly from leadData if it exists
      const customFields = leadData.customFields || {};

      // Simplified payload for local backend
      const leadPayload = {
        title: leadData.title || 'New Lead',
        name: leadData.name,
        email: leadData.email,
        phone: leadData.phone,
        company: leadData.company,
        source: leadData.source || 'website',
        budget: leadData.budget,
        status: leadData.status || 'new',
        priority: leadData.priority || 'medium',
        notes: leadData.notes || '',
        value: leadData.value || 0,
        customFields: customFields, // Use custom fields from form
        // Include assignment data
        assignedTo: leadData.assignedTo || [],
        assignedEmployees: leadData.assignedEmployees || [],
        createdAt: new Date().toISOString(),
        lastContact: new Date().toISOString()
      };

      console.log('📤 Sending lead payload:', leadPayload);
      console.log('📤 Custom fields in payload:', leadPayload.customFields);
      console.log('📤 Assignment data:', { 
        assignedTo: leadPayload.assignedTo, 
        assignedEmployees: leadPayload.assignedEmployees 
      });
      const response = await api.post(API_ENDPOINTS.LEADS, leadPayload);
      console.log('✅ Lead created successfully:', response.data);

      const createdLead = response.data?.lead || response.data?.data || response.data;
      const leadId = createdLead?._id || createdLead?.id;

      // Send notifications to all assigned employees
      if (leadPayload.assignedTo && leadPayload.assignedTo.length > 0 && leadId) {
        try {
          // 1. Send bulk notifications to global backend
          await authApi.post('/notifications/bulk', {
            recipients: leadPayload.assignedTo,
            type: 'lead',
            title: 'Lead Assigned',
            message: `Lead "${leadPayload.name}" has been assigned to you.`,
            meta: {
              leadId: leadId,
              companyName: leadPayload.company || ''
            }
          });

          // 2. Save assignment notification inside Lead backend
          for (const empId of leadPayload.assignedTo) {
            await LeadAssignmentNotificationService.createAssignmentNotification({
              leadId: leadId,
              assignedTo: empId,
              notes: leadPayload.notes || 'Lead assigned on creation',
              priority: leadPayload.priority || 'medium'
            });
          }

          // 3. Dispatch event to notify UI
          window.dispatchEvent(new Event('lead-assigned'));
        } catch (err) {
          console.error('❌ Failed to send assignment notifications during lead creation:', err);
        }
      }

      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Error creating lead:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      return { success: false, error: error.response?.data?.message || 'Failed to create lead. Please ensure the leads API is deployed.' };
    }
  }

  // Get all leads
  static async getAllLeads() {
    try {
      console.log('🔍 Fetching leads from:', API_ENDPOINTS.LEADS);
      console.log('🔍 Full API URL:', api.defaults.baseURL + API_ENDPOINTS.LEADS);
      
      // Decode user role and company ID
      const token = localStorage.getItem('authToken');
      let userRole = '';
      let companyId = '';
      let userId = '';
      
      if (token) {
        try {
          const cleanToken = token.replace(/['"]/g, '').trim();
          const decoded = jwtDecode(cleanToken);
          userRole = String(decoded.role || '').toLowerCase();
          companyId = decoded.companyId || decoded.company?._id || decoded.company || decoded.id;
          if (companyId && typeof companyId === 'object') {
            companyId = companyId._id || companyId.id;
          }
          userId = decoded.userId || decoded.id;
        } catch (err) {
          console.error('Failed to decode token in LeadsService:', err);
        }
      }
      
      // Fallback to localStorage keys if token decode didn't have it
      if (!userId) {
        const possibleKeys = ['userId', 'user_id', 'id', 'employeeId', 'employee_id'];
        for (const key of possibleKeys) {
          const val = localStorage.getItem(key);
          if (val) {
            userId = val.replace(/['"]/g, '').trim();
            break;
          }
        }
      }

      console.log('👤 Leads Filter State:', { userRole, companyId, userId });

      // Request all leads with x-bypass-auth to let client-side role filter handle it.
      // This solves the backend limitation where managers are treated as employees or company leads are restricted.
      const response = await api.get(API_ENDPOINTS.LEADS, {
        headers: {
          'x-bypass-auth': 'true'
        }
      });
      console.log('📊 Leads API response status:', response.status);
      
      // Extract leads from backend response
      const responseData = response.data;
      let leads = [];
      
      // Handle different response formats - backend returns { leads: [...] }
      if (responseData.leads && Array.isArray(responseData.leads)) {
        leads = responseData.leads;
        console.log('✅ Using leads array from response');
      } else if (responseData.data && Array.isArray(responseData.data)) {
        leads = responseData.data;
        console.log('✅ Using data array from response');
      } else if (Array.isArray(responseData)) {
        leads = responseData;
        console.log('✅ Using direct array response');
      } else {
        console.warn('⚠️ Unexpected response format, returning empty array');
        leads = [];
      }
      
      console.log(`📊 Raw database found: ${leads.length} leads`);

      // Client-side role-based and assignment-based filtering
      if (userRole === 'superadmin') {
        // Superadmin sees everything
        console.log('👑 Superadmin role: returning all leads');
      } else if (userRole === 'company' || userRole === 'franchise') {
        // Company/franchise sees only leads belonging to their company
        if (companyId) {
          leads = leads.filter(lead => {
            const leadCompanyId = lead.companyId || lead.company;
            const cleanLeadCompanyId = typeof leadCompanyId === 'object' ? (leadCompanyId?._id || leadCompanyId?.id) : leadCompanyId;
            return String(cleanLeadCompanyId) === String(companyId);
          });
          console.log(`🏢 Company role: filtered to company ${companyId} (${leads.length} leads)`);
        }
      } else if (userRole === 'manager') {
        // Manager sees leads belonging to their company OR leads explicitly assigned to them (even if company differs)
        leads = leads.filter(lead => {
          const leadCompanyId = lead.companyId || lead.company;
          const cleanLeadCompanyId = typeof leadCompanyId === 'object' ? (leadCompanyId?._id || leadCompanyId?.id) : leadCompanyId;
          const belongsToCompany = companyId && String(cleanLeadCompanyId) === String(companyId);
          
          const isAssignedToUser = lead.assignedTo && Array.isArray(lead.assignedTo) 
            ? lead.assignedTo.includes(userId) 
            : String(lead.assignedTo) === String(userId);
            
          const isAssignedInEmployees = lead.assignedEmployees && Array.isArray(lead.assignedEmployees)
            ? lead.assignedEmployees.some(emp => String(emp.employeeId || emp._id || emp.id) === String(userId))
            : false;
            
          return belongsToCompany || isAssignedToUser || isAssignedInEmployees;
        });
        console.log(`💼 Manager role: filtered to company ${companyId} or assigned to ${userId} (${leads.length} leads)`);
      } else {
        // Regular employees see ONLY leads explicitly assigned to them
        leads = leads.filter(lead => {
          const isAssignedToUser = lead.assignedTo && Array.isArray(lead.assignedTo) 
            ? lead.assignedTo.includes(userId) 
            : String(lead.assignedTo) === String(userId);
            
          const isAssignedInEmployees = lead.assignedEmployees && Array.isArray(lead.assignedEmployees)
            ? lead.assignedEmployees.some(emp => String(emp.employeeId || emp._id || emp.id) === String(userId))
            : false;
            
          return isAssignedToUser || isAssignedInEmployees;
        });
        console.log(`👤 Employee role: filtered to assigned leads for ${userId} (${leads.length} leads)`);
      }
      
      return { success: true, data: leads };
    } catch (error) {
      console.error('❌ Error fetching leads:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 
               `Failed to fetch leads: ${error.message}` 
      };
    }
  }

  // Get a single lead by ID
  static async getLeadById(leadId) {
    console.log('LeadsService: Fetching lead by ID:', leadId);
    console.log('LeadsService: API endpoint:', API_ENDPOINTS.LEAD_BY_ID(leadId));
    try {
      const response = await api.get(API_ENDPOINTS.LEAD_BY_ID(leadId));
      console.log('LeadsService: Lead API response:', response);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('LeadsService: Error fetching lead:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to fetch lead' };
    }
  }

  // Update a lead
  static async updateLead(leadId, leadData) {
    const leadPayload = {
      title: leadData.title,
      name: leadData.name,
      email: leadData.email,
      phone: leadData.phone,
      company: leadData.company,
      source: leadData.source,
      budget: leadData.budget,
      status: leadData.status,
      statusRemark: leadData.statusRemark || '',
      priority: leadData.priority || 'medium',
      value: leadData.value || 0,
      notes: leadData.notes || '',
      customFields: leadData.customFields || {}, // Include custom fields
      assignedTo: leadData.assignedTo || [],
      assignedEmployees: leadData.assignedEmployees || [],
      lastContact: new Date().toISOString()
    };

    try {
      console.log('🔄 Updating lead:', leadId, 'with status:', leadData.status, 'remark:', leadData.statusRemark);
      console.log('🔄 Custom fields in update:', leadPayload.customFields);
      const response = await api.put(API_ENDPOINTS.LEAD_BY_ID(leadId), leadPayload);
      console.log('✅ Lead updated successfully:', response.data);

      // Send notifications to all assigned employees on update
      if (leadPayload.assignedTo && leadPayload.assignedTo.length > 0) {
        try {
          // 1. Send bulk notifications to global backend
          await authApi.post('/notifications/bulk', {
            recipients: leadPayload.assignedTo,
            type: 'lead',
            title: 'Lead Updated/Assigned',
            message: `Lead "${leadPayload.name}" has been updated/assigned to you.`,
            meta: {
              leadId: leadId,
              companyName: leadPayload.company || ''
            }
          });

          // 2. Save assignment notification inside Lead backend
          for (const empId of leadPayload.assignedTo) {
            await LeadAssignmentNotificationService.createAssignmentNotification({
              leadId: leadId,
              assignedTo: empId,
              notes: leadPayload.notes || 'Lead updated',
              priority: leadPayload.priority || 'medium'
            });
          }

          // 3. Dispatch event to notify UI
          window.dispatchEvent(new Event('lead-assigned'));
        } catch (err) {
          console.error('❌ Failed to send assignment notifications during lead update:', err);
        }
      }

      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Error updating lead:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      return { success: false, error: error.response?.data?.message || 'Failed to update lead. Please ensure the leads API is deployed.' };
    }
  }

  // Delete a lead
  static async deleteLead(leadId) {
    try {
      await api.delete(API_ENDPOINTS.LEAD_BY_ID(leadId));
      
      // Import notification service and clean up notifications for this lead
      const notificationService = (await import('./notificationService')).default;
      notificationService.deleteNotificationsForLead(leadId);
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting lead:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to delete lead. Please ensure the leads API is deployed.' };
    }
  }

  // Update lead status
  static async updateLeadStatus(leadId, status) {
    try {
      console.log('LeadsService: Updating lead status:', { leadId, status });
      console.log('LeadsService: API endpoint:', API_ENDPOINTS.LEAD_BY_ID(leadId));
      
      const payload = { 
        status: status,
        lastContact: new Date().toISOString()
      };
      
      console.log('LeadsService: Payload:', payload);
      
      const response = await api.put(API_ENDPOINTS.LEAD_BY_ID(leadId), payload);
      console.log('LeadsService: Status update response:', response.data);
      
      return { success: true, data: response.data };
    } catch (error) {
      console.error('LeadsService: Error updating lead status:', error);
      console.error('LeadsService: Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      return { 
        success: false, 
        error: error.response?.data?.message || `Failed to update lead status: ${error.message}` 
      };
    }
  }

  // Search leads
  static async searchLeads(searchTerm) {
    try {
      const response = await api.get(`${API_ENDPOINTS.LEADS}?search=${encodeURIComponent(searchTerm)}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error searching leads:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to search leads' };
    }
  }

  // Get leads by status
  static async getLeadsByStatus(status) {
    try {
      const response = await api.get(`${API_ENDPOINTS.LEADS}?status=${status}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching leads by status:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to fetch leads by status' };
    }
  }
}

export default LeadsService;
